// Modale « Parler à un commercial » (templates/components/anima/modale-commercial.twig).
// S'ouvre sur tout lien vers #parler-a-un-commercial (-acheteur, -producteur) ou portant
// data-anima-lead="choix|acheteur|producteur" ; se ferme au voile, au bouton, à Échap, et en
// tirant la poignée vers le bas sur mobile. Le focus reste dans la fenêtre et revient au
// déclencheur à la fermeture (maquette « 30 sept »). Un déclencheur peut porter
// data-anima-lead-titre (« Contacter Sofruce ») : il remplace le titre du choix.

import {
  closeDialog,
  openDialog,
  swipeToClose,
  trapFocus
} from './dialogue-anima'
import {
  initEtapes,
  showStep,
  stepComplete
} from './etapes-anima'

const HASH = 'parler-a-un-commercial'
const FOCUS_DELAY = 60 // ms, laisse le panneau s'afficher avant de déplacer le focus

const init = () => {
  const modal = document.querySelector('[data-anima-lead-modal]')
  if (!modal) {
    return
  }
  const back = modal.querySelector('[data-anima-lead-back]')
  const kicker = modal.querySelector('[data-anima-lead-kicker]')
  const prog = modal.querySelector('[data-anima-lead-prog]')
  const bar = prog.querySelector('[role="progressbar"]')
  const foot = modal.querySelector('[data-anima-lead-foot]')
  const next = foot.querySelector('[data-anima-lead-next]')
  const body = modal.querySelector('[data-anima-lead-body]')
  const title = modal.querySelector('#anima-lead-title')
  const defaultTitle = title ? title.textContent : ''
  const defaultKicker = kicker.textContent
  let opener = null
  let panel = null

  // Formulaires en étapes : `forms` associe chaque panneau à l'état de son formulaire.
  let forms = new Map()
  const current = () => forms.get(panel)

  const refresh = () => {
    const state = current()
    const inForm = panel && panel.dataset.animaLeadPanel !== 'choix'
    back.hidden = !inForm || Boolean(state && state.done)
    kicker.textContent = inForm ? panel.dataset.animaLeadKickerText : defaultKicker
    const stepping = state && !state.done
    prog.hidden = !stepping
    foot.hidden = !stepping || state.index >= state.steps.length
    if (!stepping) {
      return
    }
    const n = state.steps.length
    const pos = Math.min(state.index + 1, n)
    bar.setAttribute('aria-valuemax', n)
    bar.setAttribute('aria-valuenow', pos)
    bar.setAttribute('aria-valuetext', `${pos} / ${n}`)
    prog.style.setProperty('--anima-prog', state.index >= n ? 1 : pos / n)
    next.disabled = !stepComplete(state)
    next.firstElementChild.textContent = state.index === n - 1 ? next.dataset.recap : next.dataset.suivant
  }

  const go = (state, index, dir = index > state.index ? 1 : -1) => {
    showStep(state, index, dir)
    body.scrollTop = 0
    refresh()
  }

  const show = (step) => {
    modal.querySelectorAll('[data-anima-lead-panel]').forEach((p) => {
      p.hidden = p.dataset.animaLeadPanel !== step
      if (!p.hidden) {
        panel = p
      }
    })
    const state = current()
    if (state) {
      if (state.done) {
        state.form.reset()
        state.form.hidden = false
        state.panel.querySelector('[data-anima-lead-merci]').hidden = true
        state.done = false
      }
      go(state, 0, 1)

      return
    }
    // Formulaire simple déjà envoyé : il revient vide à la réouverture.
    const merciBloc = panel.querySelector('[data-anima-lead-merci]')
    const form = panel.querySelector('.wpcf7-form')
    if (merciBloc && form && !merciBloc.hidden) {
      form.reset()
      form.hidden = false
      merciBloc.hidden = true
    }
    refresh()
    // Premier champ réellement focalisable (CF7 pose d'abord des input cachés).
    const focusable = panel.querySelector('input:not([type="hidden"]), select, textarea, button')
    if (focusable) {
      setTimeout(() => focusable.focus({
        preventScroll: true
      }), FOCUS_DELAY)
    }
  }

  forms = initEtapes(modal, {
    refresh,
    go
  })

  back.addEventListener('click', () => {
    const state = current()
    if (state && state.index > 0) {
      go(state, state.index - 1, -1)
    } else {
      show('choix')
    }
  })
  next.addEventListener('click', () => {
    const state = current()
    if (state && stepComplete(state)) {
      go(state, state.index + 1, 1)
    }
  })

  const open = (step = 'choix', titre = '') => {
    opener = document.activeElement
    if (title) {
      title.textContent = titre || defaultTitle
    }
    openDialog(modal)
    show(step)
  }

  const close = () => {
    if (modal.hidden) {
      return
    }
    closeDialog(modal, () => {
      if (opener) {
        opener.focus({
          preventScroll: true
        })
      }
    })
    if (window.location.hash.startsWith(`#${HASH}`)) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }

  const stepFromHash = (hash) => {
    const m = hash.match(new RegExp(`^#${HASH}(?:-(acheteur|producteur))?$`))
    return m ? m[1] || 'choix' : null
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest(`[data-anima-lead], a[href*="#${HASH}"]`)
    if (trigger && !modal.contains(trigger)) {
      e.preventDefault()
      const href = trigger.getAttribute('href') || ''
      open(trigger.dataset.animaLead || stepFromHash(href.slice(href.indexOf('#'))) || 'choix', trigger.dataset.animaLeadTitre)
      return
    }
    const step = e.target.closest('[data-anima-lead-step]')
    if (step && modal.contains(step)) {
      show(step.dataset.animaLeadStep)
      return
    }
    if (e.target.closest('[data-anima-lead-close]')) {
      close()
    }
  })

  document.addEventListener('keydown', (e) => {
    if (modal.hidden) {
      return
    }
    if (e.key === 'Escape') {
      close()
    }
    trapFocus(modal, e)
  })

  const handle = modal.querySelector('.anima-modale-handle')
  if (handle) {
    swipeToClose(handle, modal.querySelector('.anima-modale-box'), close)
  }

  const initial = stepFromHash(window.location.hash)
  if (initial) {
    open(initial)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
